import { Button, Col, Row, Space } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileParsedModel } from "../../../../../models/scenario-frontier.model";
import { ScenarioFrontierService } from "../../../../../services/scenario-frontier.service";
import { DownloadIcon, RefreshIcon } from "../../../../atoms/icon";
import { toggleLoading } from "../../../../atoms/loading/loading";
import { quarters } from "../../select-period";
import FrontierChartOutput from "./chart-view";
import "./index.scss";
import FrontierTableOutput from "./table-view";

interface Props {
    id: number | undefined;
    year: number;
    quarter: number;
    data: any[];
    includeConcentrationStressTest?: boolean;
}

const FrontierOutput = (props: Props) => {

    const { t } = useTranslation();
    const [showChart, setShowChart] = useState<boolean>(false);
    const [data, setData] = useState<FileParsedModel[]>([]);

    const [printing, setPrinting] = useState(false);

    const switchView = () => {
        setShowChart(pre => !pre);
    }

    const convertFileOutput = (value: any) => {
        const result: FileParsedModel = {
            file_data: [],
            file_md: {},
            file_type: value.file_type
        }

        if (value.file_data) {
            let data = [];
            try {
                data = JSON.parse(value.file_data);
                result.file_data = data;
            } catch {
                console.error("can not parse fileData");
            }
        }

        if (value.file_md) {
            try {
                const data = JSON.parse(value.file_md);
                result.file_md = data;
            } catch (error) {
                console.error(error);
            }
        }

        return result;
    }

    const downloadExcel = useCallback(() => {
        if (props.id) {
            var quarter = quarters.find(x => x.value === props.quarter)?.text;
            ScenarioFrontierService.downloadExcel(props.id).then(res => {
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `ScenarioReport-${props.year}-${quarter}.xlsx`); //or any other extension
                document.body.appendChild(link);
                link.click();
            });
        }
    }, [props.id, props.year, props.quarter])

    const exportPdf = useCallback(() => {

        setPrinting(true);

        var css = `.info-wrapper,
                    .fixed-action,
                    .review-wrapper,
                    .no-print,
                    .sider-bar,
                    .header-wrapper,
                    .review-wrapper {
                    display: none !important;
                    }

                    .ant-layout.site-layout {
                    margin-left: 0 !important;
                    padding-top: 0 !important;
                    }

                    @page {
                    size: auto;
                    margin: 0mm;
                    }`;
        const head = document.head || document.getElementsByTagName('head')[0];
        const style: any = document.createElement('style');

        head.appendChild(style);

        style.type = 'text/css';
        style.media = 'print'
        if (style.styleSheet) {
            // This is required for IE8 and below.
            style.styleSheet.cssText = css;
        } else {
            style.appendChild(document.createTextNode(css));
        }

        toggleLoading(true);
        setTimeout(() => {
            toggleLoading(false);
            window.print();
            style.remove();
            setPrinting(false);
        }, 500)
    }, [])

    useEffect(() => {
        if (props.data) {
            const values: any[] = [];
            props.data.forEach(element => {
                values.push(convertFileOutput(element));
            });

            setData(values);
        } else {
            setData([]);
        }
    }, [props.data])

    return (
        <div className="frontier-output-wrapper">
            <Row>
                <Col span={16}>
                    <h4>
                        {t("ScenarioFrontier.lbOutputs")}
                    </h4>
                    <p className="sub-title">
                        {props.year} - {quarters.find(x => x.value === props.quarter)?.text}
                    </p>
                </Col>
                <Col span={8} className="switch-wrapper no-print">
                    <Space>

                        <Button type="default" shape="round" onClick={exportPdf} > <DownloadIcon className="svg-icon" /> Export to PDF</Button>

                        <Button type="default" shape="round" onClick={downloadExcel} > <DownloadIcon className="svg-icon" /> Download as Excel</Button>

                        <Button type="default" shape="round" onClick={switchView} > <RefreshIcon className="svg-icon" /> {showChart ? t("ScenarioFrontier.btnSwitchToTableView") : t("ScenarioFrontier.btnSwitchToChartView")}</Button>

                    </Space>
                </Col>
            </Row>

            <Row id="printWrapper">
                <Col span={24}>

                    {(!showChart || printing) && <FrontierTableOutput year={props.year} data={data}
                        includeConcentrationStressTest={props.includeConcentrationStressTest} />}

                    {(showChart || printing) && <FrontierChartOutput year={props.year} data={data} />}

                </Col>
            </Row>
        </div>
    );
}

export default FrontierOutput;